import { useAtomValue, useSetAtom } from 'jotai';

import { Stack } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import {
	FiltersDrawerCheckboxGroup,
	type FiltersDrawerCheckboxGroupItem,
} from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { accountFilterAtom, updateAccountFilterAtom } from '../../atoms/leadsAtoms';
import { useAccountNameMap } from '../../hooks/useAccountNameMap';
import { useLeadsFacetCounts } from '../../hooks/useLeadsFacetCounts';

/**
 * FiltersDrawerAccountSection component that provides account filtering options
 * wrapped in an accordion. Maps svpId values to account names for display.
 */
export function FiltersDrawerAccountSection() {
	const accounts = useAtomValue(accountFilterAtom);
	const setAccounts = useSetAtom(updateAccountFilterAtom);

	// Get dynamic facet counts from the search response
	const { accountCounts } = useLeadsFacetCounts();

	// Map svpId to account names for display
	const accountNameMap = useAccountNameMap();

	// Build account items with dynamic counts, displaying names instead of IDs
	const accountItems: FiltersDrawerCheckboxGroupItem[] = Object.keys(accountCounts)
		.sort((a, b) => {
			const nameA = accountNameMap.get(a) ?? a;
			const nameB = accountNameMap.get(b) ?? b;
			return nameA.localeCompare(nameB);
		})
		.map((svpId) => ({
			value: svpId,
			label: accountNameMap.get(svpId) ?? svpId,
			count: accountCounts[svpId] ?? 0,
		}));

	// Determine if this section has any applied filters
	const hasAppliedFilters = accounts.length > 0;

	return (
		<AccordionSection title='Account' defaultExpanded={hasAppliedFilters}>
			<Stack itemsSpacing='24'>
				<FiltersDrawerCheckboxGroup
					label='Account name'
					items={accountItems}
					value={accounts}
					onChange={setAccounts}
				/>
			</Stack>
		</AccordionSection>
	);
}
